// TourTypeAddonConfig.tsx
// Admin Component for configuring tour-type-specific addon auto-calculation rules
// Allows configuring which addons are auto-added for STANDARD/PREMIUM/PRIVAT tours

import React, { useState, useEffect } from 'react';
import {
  PlusCircleIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  XCircleIcon,
  GiftIcon,
  CalculatorIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import './CustomerManagement.css';
import { API_BASE_URL, authFetch } from '../../config/api.config';
import { ONLINE_ANGEBOT_OPTIONEN, OnlineAngebot, abzeichen, felderVon, onlineAngebotVon, optionalHinweis } from '../../utils/zubuchung';

// Types
interface TourTypeAddonConfigDto {
  id: number;
  tourType: string;
  addonServiceId: number;
  addonServiceName?: string;
  addOnServiceName?: string;
  addonServiceDescription?: string;
  addOnServiceDescription?: string;
  pricingType: string;
  includedQuantity: number;
  priceOverride?: number;
  effectivePrice: number;
  isAutoAdd: boolean;
  sortOrder: number;
  isIncludedInTourPrice: boolean;
  isOptionalChoice?: boolean;
  quantityMultiplier: number;
  isOptionalExtra?: boolean; // T-058 Stufe 2 (v1.8.61): optional zubuchbar
  optionalUnzulaessig?: boolean; // Review v1.8.61: Optional gesetzt, aber nicht zulässig (nicht im Online-Angebot)
}

interface CreateTourTypeAddonConfigDto {
  tourType: string;
  addonServiceId: number;
  includedQuantity: number;
  priceOverride?: number;
  isAutoAdd: boolean;
  isIncludedInTourPrice: boolean;
  quantityMultiplier: number;
  isOptionalExtra: boolean;
}

interface UpdateTourTypeAddonConfigDto {
  includedQuantity: number;
  priceOverride?: number;
  isAutoAdd: boolean;
  sortOrder: number;
  isIncludedInTourPrice: boolean;
  quantityMultiplier: number;
  isOptionalExtra: boolean;
}

// T-058 Stufe 2 (v1.8.61): Farben der Abzeichen je Online-Angebot
const ABZEICHEN_FARBE: Record<OnlineAngebot, string> = {
  automatisch: '#10b981',
  optional: '#2563eb',
  verwaltung: '#64748b',
};

interface AddonService {
  id: number;
  name: string;
  description: string;
  price: number;
  pricingType: string;
  isActive: boolean;
}

interface CalculatedAddonDto {
  addonServiceId?: number;
  addonServiceName: string;
  pricingType: string;
  quantityTotal: number;
  quantityFree: number;
  quantityPaid: number;
  pricePerUnit: number;
  grossPricePerUnit?: number; // Bruttopreis pro Einheit
  taxRate?: number; // Steuersatz (z.B. 0.19 für 19%, 0.07 für 7%)
  subtotal: number;
}

interface TourType {
  id: number;
  code: string;
  name: string;
  description?: string;
  colorCode?: string;
  isActive: boolean;
}

const TourTypeAddonConfig: React.FC = () => {
  const [tourTypes, setTourTypes] = useState<TourType[]>([]);
  const [selectedTourType, setSelectedTourType] = useState<string>('');
  const [configs, setConfigs] = useState<TourTypeAddonConfigDto[]>([]);
  const [availableAddons, setAvailableAddons] = useState<AddonService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCalculatorModal, setShowCalculatorModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<TourTypeAddonConfigDto | null>(null);

  // Form states
  const [formData, setFormData] = useState<UpdateTourTypeAddonConfigDto>({
    includedQuantity: 0,
    priceOverride: undefined,
    isAutoAdd: true,
    sortOrder: 1,
    isIncludedInTourPrice: false,
    quantityMultiplier: 1,
    isOptionalExtra: false
  });

  const [newConfigData, setNewConfigData] = useState<CreateTourTypeAddonConfigDto>({
    tourType: selectedTourType || '',
    addonServiceId: 0,
    includedQuantity: 0,
    priceOverride: undefined,
    isAutoAdd: true,
    isIncludedInTourPrice: false,
    quantityMultiplier: 1,
    isOptionalExtra: false
  });

  // Calculator states
  const [participantCount, setParticipantCount] = useState(30);
  const [calculatedAddons, setCalculatedAddons] = useState<CalculatedAddonDto[]>([]);

  // Use central API configuration
  const API_BASE = API_BASE_URL;

  // Load tour types on component mount
  useEffect(() => {
    loadTourTypes();
  }, []);

  // Load configs when tour type changes
  useEffect(() => {
    if (selectedTourType) {
      fetchConfigs();
      fetchAvailableAddons();
      // Update newConfigData with new tourType
      setNewConfigData(prev => ({ ...prev, tourType: selectedTourType }));
    }
  }, [selectedTourType]);

  const loadTourTypes = async () => {
    try {
      // T-009: authFetch setzt die Auth-Header und refresht bei 401 automatisch
      const response = await authFetch(`${API_BASE}/tour-types`, {});

      if (!response.ok) throw new Error('Fehler beim Laden der Tour-Typen');

      const data: TourType[] = await response.json();
      const activeTourTypes = data.filter(tt => tt.isActive).sort((a, b) => a.code.localeCompare(b.code));
      setTourTypes(activeTourTypes);

      // Set first tour type as default if not already selected
      if (activeTourTypes.length > 0 && !selectedTourType) {
        setSelectedTourType(activeTourTypes[0].code);
      }
    } catch (err: any) {
      console.error('Error loading tour types:', err);
      setError('Fehler beim Laden der Tour-Typen');
    }
  };

  const fetchConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      // URL-encode the tour type to handle spaces and special characters
      const encodedTourType = encodeURIComponent(selectedTourType);
      const response = await authFetch(`${API_BASE}/tour-types/${encodedTourType}/addons`, {});

      if (!response.ok) throw new Error('Fehler beim Laden der Konfigurationen');

      const data = await response.json();
      console.log('🔍 DEBUG: Loaded addon configs:', data);
      console.log('🔍 DEBUG: First config object:', data[0]);
      setConfigs(data);
    } catch (err: any) {
      console.error('Error fetching configs:', err);
      setError('Fehler beim Laden der Addon-Konfigurationen');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableAddons = async () => {
    try {
      // First, get all categories
      const categoriesResponse = await authFetch(`${API_BASE}/addon/categories?includeInactive=false`, {});

      if (!categoriesResponse.ok) throw new Error('Fehler beim Laden der Kategorien');

      const categories = await categoriesResponse.json();

      // Then, get all services from all categories
      const allServices: AddonService[] = [];
      for (const category of categories) {
        const servicesResponse = await authFetch(`${API_BASE}/addon/categories/${category.id}/services`, {});

        if (servicesResponse.ok) {
          const services = await servicesResponse.json();
          allServices.push(...services);
        }
      }

      setAvailableAddons(allServices.filter((a: AddonService) => a.isActive));
    } catch (err: any) {
      console.error('Error fetching addons:', err);
    }
  };

  const handleAddConfig = async () => {
    try {
      const requestData = { ...newConfigData, tourType: selectedTourType };
      console.log('🔍 DEBUG: Sending addon config:', requestData);

      // URL-encode the tour type
      const encodedTourType = encodeURIComponent(selectedTourType);
      const response = await authFetch(`${API_BASE}/tour-types/${encodedTourType}/addons`, {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      console.log('🔍 DEBUG: Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 DEBUG: Error response:', errorText);
        throw new Error(`Fehler beim Hinzufügen: ${response.status} - ${errorText}`);
      }

      alert('Addon erfolgreich konfiguriert');
      setShowAddModal(false);
      fetchConfigs();
    } catch (err: any) {
      console.error('Error adding config:', err);
      alert('Fehler: ' + err.message);
    }
  };

  const handleUpdateConfig = async () => {
    if (!selectedConfig) return;

    try {
      // URL-encode the tour type
      const encodedTourType = encodeURIComponent(selectedTourType);
      const response = await authFetch(`${API_BASE}/tour-types/${encodedTourType}/addons/${selectedConfig.id}`, {
        method: 'PUT',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        // T-058 (v1.8.61): Prüftext des Servers anzeigen (z. B. "Optional zubuchbar" nur für kostenpflichtige Leistungen)
        const errorText = await response.text();
        throw new Error(errorText || 'Fehler beim Aktualisieren');
      }

      alert('Konfiguration erfolgreich aktualisiert');
      setShowEditModal(false);
      fetchConfigs();
    } catch (err: any) {
      console.error('Error updating config:', err);
      alert('Fehler: ' + err.message);
    }
  };

  const handleDeleteConfig = async (configId: number) => {
    if (!window.confirm('Möchten Sie diese Konfiguration wirklich löschen?')) return;

    try {
      // URL-encode the tour type
      const encodedTourType = encodeURIComponent(selectedTourType);
      const response = await authFetch(`${API_BASE}/tour-types/${encodedTourType}/addons/${configId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Fehler beim Löschen');

      alert('Konfiguration erfolgreich gelöscht');
      fetchConfigs();
    } catch (err: any) {
      console.error('Error deleting config:', err);
      alert('Fehler: ' + err.message);
    }
  };

  const handleCalculate = async () => {
    try {
      // URL-encode the tour type
      const encodedTourType = encodeURIComponent(selectedTourType);
      const response = await fetch(`${API_BASE}/tour-types/${encodedTourType}/addons/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tourType: selectedTourType,
          participantCount: participantCount
        })
      });

      if (!response.ok) throw new Error('Fehler bei der Berechnung');

      const data = await response.json();
      setCalculatedAddons(data);
      setShowCalculatorModal(true);
    } catch (err: any) {
      console.error('Error calculating:', err);
      alert('Fehler bei der Berechnung: ' + err.message);
    }
  };

  const getPricingTypeLabel = (type: string) => {
    switch(type) {
      case 'PerPerson': return 'Pro Person';
      case 'PerGroup': return 'Pro Gruppe';
      case 'PerTrip': return 'Pro Fahrt';
      case 'PerDay': return 'Pro Tag';
      case 'PerNight': return 'Pro Nacht';
      case 'Fixed': return 'Festpreis';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="customer-management loading">
        <div className="spinner">
          <ArrowPathIcon className="w-12 h-12 animate-spin" />
        </div>
      </div>
    );
  }

  const totalAddonsPrice = calculatedAddons.reduce((sum, addon) => sum + addon.subtotal, 0);

  return (
    <div className="customer-management">
      {/* Header */}
      <div className="customer-header">
        <div className="header-top">
          <h2>Tour-Typ Addon-Konfiguration</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => {
                setNewConfigData({
                  tourType: selectedTourType,
                  addonServiceId: availableAddons[0]?.id || 0,
                  includedQuantity: 0,
                  priceOverride: undefined,
                  isAutoAdd: true,
                  isIncludedInTourPrice: false,
                  quantityMultiplier: 1,
                  isOptionalExtra: false
                });
                setShowAddModal(true);
              }}
              className="btn-new-customer"
              disabled={availableAddons.length === 0}
            >
              <PlusCircleIcon className="w-5 h-5" />
              Addon hinzufügen
            </button>
            <button
              onClick={handleCalculate}
              className="btn-secondary"
              style={{ padding: '10px 15px', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <CalculatorIcon className="w-5 h-5" />
              Berechnung testen
            </button>
          </div>
        </div>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Konfigurieren Sie, welche Zusatzoptionen automatisch bei Buchungen für diesen Tour-Typ hinzugefügt werden
          und wie viele Einheiten inklusive sind.
        </p>
      </div>

      {/* Tour Type Tabs */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '2rem',
        borderBottom: '2px solid #e0e0e0',
        paddingBottom: '10px',
        flexWrap: 'wrap'
      }}>
        {tourTypes.map(tourType => (
          <button
            key={tourType.code}
            onClick={() => setSelectedTourType(tourType.code)}
            style={{
              padding: '12px 24px',
              borderRadius: '8px 8px 0 0',
              border: 'none',
              borderLeft: tourType.colorCode ? `4px solid ${tourType.colorCode}` : 'none',
              background: selectedTourType === tourType.code ? '#007bff' : '#f0f0f0',
              color: selectedTourType === tourType.code ? 'white' : '#333',
              fontWeight: selectedTourType === tourType.code ? 'bold' : 'normal',
              cursor: 'pointer',
              fontSize: '16px',
              transition: 'all 0.2s'
            }}
          >
            {tourType.name}
          </button>
        ))}
      </div>

      {/* Info Box */}
      <div style={{
        background: '#e7f3ff',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '2rem',
        border: '1px solid #0066cc'
      }}>
        <h4 style={{ margin: '0 0 8px 0', color: '#0066cc' }}>
          Tour-Typ: {selectedTourType}
        </h4>
        <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>
          <strong>{configs.length}</strong> Addon(s) konfiguriert •
          <strong> {configs.filter(c => c.isAutoAdd).length}</strong> werden automatisch hinzugefügt •
          <strong> {configs.filter(c => onlineAngebotVon(c) === 'optional').length}</strong> optional zubuchbar
        </p>
      </div>

      {error && (
        <div style={{ background: '#f8d7da', color: '#721c24', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Configs Grid */}
      <div className="customers-grid">
        {configs.length === 0 ? (
          <div className="no-data">
            <PlusCircleIcon style={{ width: '48px', height: '48px', color: '#9ca3af' }} />
            <p>Keine Addons für {selectedTourType} konfiguriert</p>
            <button
              onClick={() => {
                setNewConfigData({
                  tourType: selectedTourType,
                  addonServiceId: availableAddons[0]?.id || 0,
                  includedQuantity: 0,
                  priceOverride: undefined,
                  isAutoAdd: true,
                  isIncludedInTourPrice: false,
                  quantityMultiplier: 1,
                  isOptionalExtra: false
                });
                setShowAddModal(true);
              }}
              className="btn-primary"
              style={{ marginTop: '1rem' }}
              disabled={availableAddons.length === 0}
            >
              Erstes Addon hinzufügen
            </button>
          </div>
        ) : (
          configs.map(config => (
            <div key={config.id} className="customer-card">
              <div className="customer-card-header" style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                padding: '16px',
                borderRadius: '8px 8px 0 0',
                marginBottom: '16px'
              }}>
                <div className="customer-info">
                  <h3 style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#ffffff',
                    margin: '0 0 8px 0',
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}>
                    {config.addOnServiceName || config.addonServiceName || 'Addon Name fehlt'}
                  </h3>
                  {(config.addOnServiceDescription || config.addonServiceDescription) && (
                    <p style={{
                      fontSize: '15px',
                      color: '#e2e8f0',
                      margin: '0 0 12px 0',
                      lineHeight: '1.6',
                      opacity: '0.95'
                    }}>
                      {config.addOnServiceDescription || config.addonServiceDescription}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span
                      title={config.optionalUnzulaessig ? 'Optional zubuchbar ist für diese Leistung nicht zulässig (z. B. Wahlgericht, inklusive oder Preis-Override); sie erscheint nicht im Online-Angebot. Bitte Konfiguration bearbeiten.' : undefined}
                      style={{
                      background: config.optionalUnzulaessig ? '#d97706' : ABZEICHEN_FARBE[onlineAngebotVon(config)],
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}>{abzeichen(onlineAngebotVon(config))}{config.optionalUnzulaessig ? ' (unzulässig)' : ''}</span>
                    {config.isIncludedInTourPrice && (
                      <span style={{
                        background: '#10b981',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}>ENTHALTEN</span>
                    )}
                    {config.isOptionalChoice && (
                      <span style={{
                        background: '#f59e0b',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}>Optionale Auswahl</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="customer-card-body">
                {/* Pricing Type Section */}
                <div style={{
                  background: '#f8fafc',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <UsersIcon style={{ width: '18px', height: '18px', color: '#3b82f6', flexShrink: 0 }} />
                  <div>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                      Preistyp
                    </span>
                    <span style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
                      {getPricingTypeLabel(config.pricingType)}
                    </span>
                  </div>
                </div>

                <div className="customer-stats">
                  <div className="stat-item">
                    <CurrencyEuroIcon className="w-4 h-4" />
                    <span>
                      {config.priceOverride !== null && config.priceOverride !== undefined
                        ? `${config.priceOverride.toFixed(2)}€ (Override)`
                        : `${config.effectivePrice.toFixed(2)}€`
                      }
                    </span>
                  </div>

                  <div className="stat-item">
                    <GiftIcon className="w-4 h-4" />
                    <span>{config.includedQuantity} inklusive</span>
                  </div>
                </div>

                {/* Example Calculation */}
                <div style={{
                  marginTop: '12px',
                  padding: '10px',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}>
                  <strong>Beispiel (30 Personen):</strong>
                  {config.pricingType === 'PerPerson' ? (
                    <div style={{ marginTop: '4px' }}>
                      • {config.includedQuantity} kostenlos<br/>
                      • {Math.max(0, 30 - config.includedQuantity)} × {config.effectivePrice.toFixed(2)}€ = {' '}
                      <strong>{(Math.max(0, 30 - config.includedQuantity) * config.effectivePrice).toFixed(2)}€</strong>
                    </div>
                  ) : (
                    <div style={{ marginTop: '4px' }}>
                      • {config.includedQuantity > 0 ? 'Kostenlos (inklusive)' : `${config.effectivePrice.toFixed(2)}€`}
                    </div>
                  )}
                </div>
              </div>

              <div className="customer-card-footer">
                <button
                  onClick={() => {
                    setSelectedConfig(config);
                    setFormData({
                      includedQuantity: config.includedQuantity,
                      priceOverride: config.priceOverride,
                      isAutoAdd: config.isAutoAdd,
                      sortOrder: config.sortOrder,
                      isIncludedInTourPrice: config.isIncludedInTourPrice || false,
                      quantityMultiplier: config.quantityMultiplier || 1,
                      isOptionalExtra: config.isOptionalExtra || false
                    });
                    setShowEditModal(true);
                  }}
                  className="btn-action btn-edit"
                  title="Bearbeiten"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteConfig(config.id)}
                  className="btn-action btn-delete"
                  title="Löschen"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content customer-form" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              padding: '20px 24px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '2px solid #475569'
            }}>
              <h3 style={{
                color: '#ffffff',
                fontSize: '20px',
                fontWeight: '700',
                margin: '0',
                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }}>Addon zu {selectedTourType} hinzufügen</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="btn-close"
                style={{
                  color: '#ffffff',
                  fontSize: '28px',
                  fontWeight: '300',
                  opacity: '0.9',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '0.9'}
              >×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); handleAddConfig(); }}>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Addon *</label>
                    <select
                      value={newConfigData.addonServiceId}
                      onChange={(e) => setNewConfigData({...newConfigData, addonServiceId: parseInt(e.target.value)})}
                      required
                    >
                      <option value={0}>-- Bitte wählen --</option>
                      {availableAddons.map(addon => (
                        <option key={addon.id} value={addon.id}>
                          {addon.name} ({addon.price.toFixed(2)}€ - {getPricingTypeLabel(addon.pricingType)})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Preisgestaltung Radio Buttons */}
                  <div className="form-group full-width">
                    <label>Preisgestaltung</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={newConfigData.isIncludedInTourPrice}
                          onChange={() => {
                            setNewConfigData({...newConfigData, isIncludedInTourPrice: true, priceOverride: undefined});
                          }}
                        />
                        <div>
                          <strong>Im Tour-Preis enthalten</strong>
                          <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>
                            Wird nicht extra berechnet, nur für Lieferantenliste verwendet
                          </p>
                        </div>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={!newConfigData.isIncludedInTourPrice}
                          onChange={() => setNewConfigData({...newConfigData, isIncludedInTourPrice: false})}
                        />
                        <div>
                          <strong>Zusätzlich berechnen</strong>
                          <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>
                            Wird zur Tour-Rechnung hinzuaddiert
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Menge pro Teilnehmer - nur bei Inklusivleistungen */}
                  {newConfigData.isIncludedInTourPrice && (
                    <div className="form-group full-width">
                      <label>Menge pro Teilnehmer</label>
                      <input
                        type="number"
                        value={newConfigData.quantityMultiplier}
                        onChange={(e) => setNewConfigData({...newConfigData, quantityMultiplier: Math.max(1, parseInt(e.target.value) || 1)})}
                        min="1"
                        step="1"
                        placeholder="1"
                        style={{ maxWidth: '120px' }}
                      />
                      <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                        Bei 2 erhält jeder Teilnehmer die doppelte Menge (z.B. 2 Bratwürste statt 1).
                        <br />Gilt nur für optionale Inklusivleistungen wie Verpflegungsauswahl.
                      </small>
                    </div>
                  )}

                  {!newConfigData.isIncludedInTourPrice && (
                    <>
                      <div className="form-group">
                        <label>Inklusiv-Menge</label>
                        <input
                          type="number"
                          value={newConfigData.includedQuantity}
                          onChange={(e) => setNewConfigData({...newConfigData, includedQuantity: parseInt(e.target.value)})}
                          min="0"
                          placeholder="0"
                        />
                        <small style={{ color: '#666', fontSize: '12px' }}>
                          Anzahl der kostenlosen Einheiten
                        </small>
                      </div>

                      <div className="form-group">
                        <label>Preis-Override (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={newConfigData.priceOverride || ''}
                          onChange={(e) => setNewConfigData({...newConfigData, priceOverride: e.target.value ? parseFloat(e.target.value) : undefined})}
                          min="0"
                          placeholder="Standard-Preis verwenden"
                        />
                        <small style={{ color: '#666', fontSize: '12px' }}>
                          Leer lassen für Standard-Preis
                        </small>
                      </div>
                    </>
                  )}

                  {/* T-058 Stufe 2 (v1.8.61): Online-Angebot statt Checkbox "Automatisch bei Buchung hinzufügen" */}
                  <div className="form-group full-width">
                    <label htmlFor="online-angebot-neu">Online-Angebot</label>
                    <select
                      id="online-angebot-neu"
                      value={onlineAngebotVon(newConfigData)}
                      onChange={(e) => setNewConfigData({...newConfigData, ...felderVon(e.target.value as OnlineAngebot)})}
                    >
                      {ONLINE_ANGEBOT_OPTIONEN.map(o => (
                        <option key={o.wert} value={o.wert}>{o.text}</option>
                      ))}
                    </select>
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      {ONLINE_ANGEBOT_OPTIONEN.find(o => o.wert === onlineAngebotVon(newConfigData))?.hinweis}
                    </small>
                    {onlineAngebotVon(newConfigData) === 'optional' && optionalHinweis(newConfigData.isIncludedInTourPrice, newConfigData.includedQuantity, newConfigData.priceOverride) && (
                      <small style={{ color: '#b45309', fontSize: '12px', display: 'block' }}>
                        {optionalHinweis(newConfigData.isIncludedInTourPrice, newConfigData.includedQuantity, newConfigData.priceOverride)}
                      </small>
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    Hinzufügen
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="btn-secondary"
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedConfig && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content customer-form" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              padding: '20px 24px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '2px solid #475569'
            }}>
              <h3 style={{
                color: '#ffffff',
                fontSize: '20px',
                fontWeight: '700',
                margin: '0',
                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }}>Konfiguration bearbeiten</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="btn-close"
                style={{
                  color: '#ffffff',
                  fontSize: '28px',
                  fontWeight: '300',
                  opacity: '0.9',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '0.9'}
              >×</button>
            </div>
            <div className="modal-body">
              {/* Addon Information Display - PROMINENT */}
              <div style={{
                background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
                padding: '24px',
                borderRadius: '12px',
                marginBottom: '24px',
                color: 'white',
                boxShadow: '0 6px 20px rgba(30, 64, 175, 0.4)'
              }}>
                <h2 style={{
                  margin: '0 0 12px 0',
                  fontSize: '26px',
                  fontWeight: '800',
                  color: '#ffffff',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  letterSpacing: '0.5px'
                }}>
                  {selectedConfig.addOnServiceName || selectedConfig.addonServiceName || 'Addon Name fehlt'}
                </h2>

                {(selectedConfig.addOnServiceDescription || selectedConfig.addonServiceDescription) && (
                  <p style={{
                    fontSize: '16px',
                    color: '#ffffff',
                    margin: '0 0 16px 0',
                    lineHeight: '1.7',
                    opacity: '0.98',
                    fontWeight: '500'
                  }}>
                    {selectedConfig.addOnServiceDescription || selectedConfig.addonServiceDescription}
                  </p>
                )}

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <UsersIcon style={{ width: '18px', height: '18px', color: 'white' }} />
                    <span style={{ fontSize: '15px', fontWeight: '600' }}>
                      {getPricingTypeLabel(selectedConfig.pricingType)}
                    </span>
                  </div>
                  <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '15px' }}>
                    •
                  </span>
                  <span style={{ fontSize: '15px', fontWeight: '600' }}>
                    Standardpreis: {selectedConfig.effectivePrice.toFixed(2)}€
                  </span>
                </div>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleUpdateConfig(); }}>
                <div className="form-grid">
                  {/* Preisgestaltung Radio Buttons */}
                  <div className="form-group full-width">
                    <label>Preisgestaltung</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={formData.isIncludedInTourPrice}
                          onChange={() => {
                            setFormData({...formData, isIncludedInTourPrice: true, priceOverride: undefined});
                          }}
                        />
                        <div>
                          <strong>Im Tour-Preis enthalten</strong>
                          <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>
                            Wird nicht extra berechnet, nur für Lieferantenliste verwendet
                          </p>
                        </div>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={!formData.isIncludedInTourPrice}
                          onChange={() => setFormData({...formData, isIncludedInTourPrice: false})}
                        />
                        <div>
                          <strong>Zusätzlich berechnen</strong>
                          <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>
                            Wird zur Tour-Rechnung hinzuaddiert
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Menge pro Teilnehmer - nur bei Inklusivleistungen */}
                  {formData.isIncludedInTourPrice && (
                    <div className="form-group full-width">
                      <label>Menge pro Teilnehmer</label>
                      <input
                        type="number"
                        value={formData.quantityMultiplier}
                        onChange={(e) => setFormData({...formData, quantityMultiplier: Math.max(1, parseInt(e.target.value) || 1)})}
                        min="1"
                        step="1"
                        placeholder="1"
                        style={{ maxWidth: '120px' }}
                      />
                      <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                        Bei 2 erhält jeder Teilnehmer die doppelte Menge (z.B. 2 Bratwürste statt 1).
                        <br />Gilt nur für optionale Inklusivleistungen wie Verpflegungsauswahl.
                      </small>
                    </div>
                  )}

                  {!formData.isIncludedInTourPrice && (
                    <>
                      <div className="form-group">
                        <label>Inklusiv-Menge</label>
                        <input
                          type="number"
                          value={formData.includedQuantity}
                          onChange={(e) => setFormData({...formData, includedQuantity: parseInt(e.target.value)})}
                          min="0"
                        />
                      </div>

                      <div className="form-group">
                        <label>Preis-Override (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.priceOverride || ''}
                          onChange={(e) => setFormData({...formData, priceOverride: e.target.value ? parseFloat(e.target.value) : undefined})}
                          min="0"
                          placeholder={`Standard: ${selectedConfig.effectivePrice.toFixed(2)}€`}
                        />
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <label>Sortierung</label>
                    <input
                      type="number"
                      value={formData.sortOrder}
                      onChange={(e) => setFormData({...formData, sortOrder: parseInt(e.target.value)})}
                      min="1"
                    />
                  </div>

                  {/* T-058 Stufe 2 (v1.8.61): Online-Angebot statt Checkbox "Automatisch bei Buchung hinzufügen" */}
                  <div className="form-group full-width">
                    <label htmlFor="online-angebot-bearbeiten">Online-Angebot</label>
                    <select
                      id="online-angebot-bearbeiten"
                      value={onlineAngebotVon(formData)}
                      onChange={(e) => setFormData({...formData, ...felderVon(e.target.value as OnlineAngebot)})}
                    >
                      {ONLINE_ANGEBOT_OPTIONEN.map(o => (
                        <option key={o.wert} value={o.wert}>{o.text}</option>
                      ))}
                    </select>
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      {ONLINE_ANGEBOT_OPTIONEN.find(o => o.wert === onlineAngebotVon(formData))?.hinweis}
                    </small>
                    {onlineAngebotVon(formData) === 'optional' && optionalHinweis(formData.isIncludedInTourPrice, formData.includedQuantity, formData.priceOverride) && (
                      <small style={{ color: '#b45309', fontSize: '12px', display: 'block' }}>
                        {optionalHinweis(formData.isIncludedInTourPrice, formData.includedQuantity, formData.priceOverride)}
                      </small>
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    Speichern
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="btn-secondary"
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Calculator Modal */}
      {showCalculatorModal && (
        <div className="modal-overlay" onClick={() => setShowCalculatorModal(false)}>
          <div className="modal-content customer-form" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3>Berechnung für {selectedTourType}</h3>
              <button onClick={() => setShowCalculatorModal(false)} className="btn-close">×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Teilnehmer-Anzahl:
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="number"
                    value={participantCount}
                    onChange={(e) => setParticipantCount(parseInt(e.target.value) || 0)}
                    min="1"
                    max="200"
                    style={{ padding: '10px', borderRadius: '6px', border: '1px solid #ddd', flex: 1 }}
                  />
                  <button onClick={handleCalculate} className="btn-secondary">
                    Neu berechnen
                  </button>
                </div>
              </div>

              <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 15px 0' }}>Berechnete Addons:</h4>
                {calculatedAddons.length === 0 ? (
                  <p style={{ color: '#666' }}>Keine Auto-Add Addons konfiguriert</p>
                ) : (
                  <div>
                    {calculatedAddons.map((addon, idx) => (
                      <div key={idx} style={{
                        background: 'white',
                        padding: '12px',
                        borderRadius: '6px',
                        marginBottom: '10px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                          {addon.addonServiceName}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          • Gesamt: {addon.quantityTotal} {getPricingTypeLabel(addon.pricingType)}<br/>
                          {addon.quantityFree > 0 && (
                            <span style={{ color: '#28a745' }}>
                              ✓ {addon.quantityFree} KOSTENLOS (inklusive)<br/>
                            </span>
                          )}
                          {addon.quantityPaid > 0 && (
                            <span>
                              • {addon.quantityPaid} × {addon.pricePerUnit.toFixed(2)}€ = {' '}
                              <strong style={{ color: '#dc3545' }}>{addon.subtotal.toFixed(2)}€</strong>
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    <div style={{
                      marginTop: '15px',
                      paddingTop: '15px',
                      borderTop: '2px solid #007bff',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      textAlign: 'right'
                    }}>
                      Addons Gesamt: <span style={{ color: '#007bff' }}>{totalAddonsPrice.toFixed(2)}€</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-actions" style={{ marginTop: '20px' }}>
                <button onClick={() => setShowCalculatorModal(false)} className="btn-secondary">
                  Schließen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TourTypeAddonConfig;
